/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { SectionCard } from '@features/property-management/components/PropertyDetailView/SectionCard';
import { clientRoleLabelKey } from '../../utils/client-roles';

export interface ClientRolesCardProps {
  /** The client's effective roles (`client_roles`, or the legacy `client_role`). */
  roles: string[];
  /** Opens the edit form where the roles are picked. */
  onEdit: () => void;
}

/**
 * The client's roles in the right rail: seeker, owner, sales partner,
 * exchange, developer, investor, short-term. A client can hold several.
 *
 * @param props - The roles and the edit callback.
 * @returns The roles card, with a prompt to fill them when there are none.
 */
export function ClientRolesCard({ roles, onEdit }: ClientRolesCardProps) {
  const { t } = useTranslation('features/clients');

  return (
    <SectionCard data-testid="client-roles-card" title={t('detail.roles.title')}>
      {roles.length > 0 ? (
        <div className="mb-(--space-3) flex flex-wrap gap-(--space-1-5)">
          {roles.map((role) => (
            <Text
              variant="meta"
              key={role}
              component="span"
              data-testid={`client-role-${role}`}
              className="inline-flex min-h-(--space-5) items-center rounded-(--radius-full) bg-(--surface-tertiary) px-(--space-2) [font-weight:var(--font-semibold)] text-(--text-tertiary)"
            >
              {t(clientRoleLabelKey(role), { defaultValue: role })}
            </Text>
          ))}
        </div>
      ) : (
        <div className="mb-(--space-3) flex items-center justify-between gap-(--space-2)">
          <Text variant="meta" component="span" className="text-(--text-secondary)">
            {t('detail.roles.empty')}
          </Text>
          <UnstyledButton
            type="button"
            data-testid="client-roles-fill"
            onClick={onEdit}
            className="flex-none border-none bg-transparent p-0 cursor-pointer"
          >
            <Text component="span" variant="meta" className="[font-weight:var(--font-semibold)] text-(--seed-base)">
              {t('detail.quality.fill')}
            </Text>
          </UnstyledButton>
        </div>
      )}
    </SectionCard>
  );
}
